import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ExpandMore as ExpandMoreIcon, Update } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import PromoFeature from '~/components/Promo/PromoFeature';

import useUpdatePromoCodeExpiry from '~/hooks/apis/promo/useUpdatePromoCodeExpiry';
import usePromoFeatureAvailability from '~/hooks/usePromoFeatureAvailability';

import { BulkUpdatePromoCodesByIdsResponse, GetPromoCodeProps } from '~/services/PromoService';
import { parseDayjs } from '~/services/TimeService';

import { chunkArray } from '~/utils/chunkArray';

import {
  BulkOperation,
  BulkOperationRowStatus,
  DEFAULT_PROMO_ID_LOAD_BATCH_SIZE,
  getAppliedPromoSearchFilters,
  loadAllSearchPromoIds,
} from './PromoBulkOperations';

const PROMO_EXPIRY_BATCH_SIZE = 100;

type Scope = 'page' | 'search';
type Review = { ids: Array<string>; expiresAt: string; filters: Array<string> };

interface Props {
  activeOperation: BulkOperation | null;
  currentPagePromos: Array<Pick<App.PromoData, 'id_promo_code'>>;
  totalSearchResults: number;
  isUnfilteredAllSearch: boolean;
  searchFilters: GetPromoCodeProps;
  loadSearchPage: (page: number, batchSize: number) => Promise<Array<App.PromoData>>;
  onComplete: () => Promise<void>;
  onOperationChange: (operation: BulkOperation | null) => void;
  onStatusChange: (statuses: Record<string, BulkOperationRowStatus>) => void;
}

export function getPromoExpiryUpdateResult(
  response: Pick<BulkUpdatePromoCodesByIdsResponse, 'result'>,
  requestedIds: Array<string>,
) {
  const updatedIds = response?.result?.updatedPromoCodeIds;
  const failedIds = response?.result?.failedPromoCodes?.map((promo) => promo.id_promo_code);
  if (!Array.isArray(updatedIds) || !Array.isArray(failedIds)) {
    throw new Error('Expiry update returned an invalid result. Refresh the search before retrying.');
  }
  const requested = new Set(requestedIds);
  const accountedFor = [...updatedIds, ...failedIds];
  if (
    requested.size !== requestedIds.length ||
    accountedFor.length !== requested.size ||
    new Set(accountedFor).size !== requested.size ||
    accountedFor.some((id) => !requested.has(id))
  ) {
    throw new Error(
      'Expiry update did not account for the requested promo code IDs. Refresh the search before retrying.',
    );
  }
  return { updatedIds, failedIds };
}

export default function PromoBulkExpiryUpdate({
  activeOperation,
  currentPagePromos,
  totalSearchResults,
  isUnfilteredAllSearch,
  searchFilters,
  loadSearchPage,
  onComplete,
  onOperationChange,
  onStatusChange,
}: Props) {
  const canBulkUpdatePromos = usePromoFeatureAvailability('promo-bulk-update');
  const [scope, setScope] = useState<Scope>('page');
  const [expiresAt, setExpiresAt] = useState('');
  const [loadedIds, setLoadedIds] = useState<Array<string> | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [review, setReview] = useState<Review | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{
    processed: number;
    updated: number;
    failed: number;
    total: number;
  } | null>(null);
  const [completion, setCompletion] = useState<{ updated: number; failed: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelLoadRef = useRef(false);
  const updateExpiry = useUpdatePromoCodeExpiry();
  const expanded = activeOperation === 'expiry';
  const formatExpiry = (value: string) =>
    parseDayjs(value).tz(window.configs.DEFAULT_TIMEZONE).format('DD/MM/YYYY h:mm A');

  useEffect(() => {
    cancelLoadRef.current = true;
    setLoadedIds(null);
    setReview(null);
    setConfirmOpen(false);
    setError(null);
  }, [loadSearchPage]);

  const resetReview = () => {
    setReview(null);
    setConfirmOpen(false);
    setCompletion(null);
    setProgress(null);
    setError(null);
    onStatusChange({});
  };

  const loadIds = useCallback(async () => {
    cancelLoadRef.current = false;
    setLoading(true);
    setLoadedIds(null);
    setError(null);
    try {
      const ids = await loadAllSearchPromoIds(
        totalSearchResults,
        DEFAULT_PROMO_ID_LOAD_BATCH_SIZE,
        loadSearchPage,
        () => {},
        () => cancelLoadRef.current,
      );
      if (ids) {
        setLoadedIds(ids);
      }
    } catch (loadError) {
      setError((loadError as Error).message || 'Unable to load matching promo codes.');
    } finally {
      setLoading(false);
    }
  }, [loadSearchPage, totalSearchResults]);

  const reviewUpdate = () => {
    const parsedExpiry = parseDayjs(expiresAt);
    if (!expiresAt || !parsedExpiry.isValid()) {
      setError('Select a valid expiry date and time.');
      return;
    }
    const ids = scope === 'page' ? currentPagePromos.map((promo) => promo.id_promo_code) : loadedIds;
    if (!ids?.length || new Set(ids).size !== ids.length) {
      setError('The selected promo IDs are incomplete. Refresh the search and review again.');
      return;
    }
    setReview({
      ids: [...ids],
      expiresAt: parsedExpiry.toISOString(),
      filters: getAppliedPromoSearchFilters(searchFilters),
    });
    setError(null);
    setCompletion(null);
  };

  const submitUpdate = async () => {
    if (!review || isUnfilteredAllSearch) {
      return;
    }
    setConfirmOpen(false);
    setSubmitting(true);
    setError(null);
    setCompletion(null);
    const visibleIds = new Set(currentPagePromos.map((promo) => promo.id_promo_code));
    let updated = 0;
    let failed = 0;
    let processed = 0;
    try {
      for (const batch of chunkArray(review.ids, PROMO_EXPIRY_BATCH_SIZE)) {
        onStatusChange(
          Object.fromEntries(batch.filter((id) => visibleIds.has(id)).map((id) => [id, 'pending' as const])),
        );
        const response = await updateExpiry.mutateAsync({
          promoCodeIds: batch,
          promoUpdate: {
            promo: { expires_at: review.expiresAt },
            mode: 'update-only',
            requestSource: 'admin-promo-search-bulk-expiry',
          },
        });
        const result = getPromoExpiryUpdateResult(response, batch);
        const updatedSet = new Set(result.updatedIds);
        onStatusChange(
          Object.fromEntries(
            batch.filter((id) => visibleIds.has(id)).map((id) => [id, updatedSet.has(id) ? 'updated' : 'failed']),
          ),
        );
        updated += result.updatedIds.length;
        failed += result.failedIds.length;
        processed += batch.length;
        setProgress({ processed, updated, failed, total: review.ids.length });
      }
      setCompletion({ updated, failed, total: review.ids.length });
    } catch (updateError) {
      setError((updateError as Error).message || 'Expiry update stopped. Refresh the search before retrying.');
    } finally {
      setSubmitting(false);
      setReview(null);
      setLoadedIds(null);
      void onComplete().catch((refreshError) => {
        setError((refreshError as Error).message || 'The update finished, but search results could not be refreshed.');
      });
    }
  };

  if (!canBulkUpdatePromos) {
    return null;
  }

  return (
    <PromoFeature featureKey="promo-bulk-update" presentation="raw">
      <Accordion
        expanded={expanded}
        disabled={isUnfilteredAllSearch}
        onChange={(_, nextExpanded) => {
          if (isSubmitting) {
            return;
          }
          cancelLoadRef.current = true;
          resetReview();
          onOperationChange(nextExpanded ? 'expiry' : null);
        }}
        disableGutters
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          width: expanded ? '100%' : 'fit-content',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Update color="primary" />
            <Typography fontWeight="bold">Bulk update expiry date</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Stack spacing={2} alignItems="flex-start">
            <FormControl>
              <FormLabel>1. Scope</FormLabel>
              <RadioGroup
                row
                value={scope}
                onChange={(_, value: Scope) => {
                  setScope(value);
                  resetReview();
                }}
              >
                <FormControlLabel value="page" control={<Radio />} label={`This page (${currentPagePromos.length})`} />
                <FormControlLabel
                  value="search"
                  control={<Radio />}
                  label={`All matching results (${totalSearchResults})`}
                />
              </RadioGroup>
            </FormControl>
            {scope === 'search' && !loadedIds && (
              <Button variant="text" disabled={isLoading || !totalSearchResults} onClick={() => void loadIds()}>
                {isLoading ? 'Loading promo IDs...' : 'Load matching promo IDs'}
              </Button>
            )}
            {isLoading && (
              <Button
                variant="text"
                onClick={() => {
                  cancelLoadRef.current = true;
                }}
              >
                Cancel loading
              </Button>
            )}
            {loadedIds && scope === 'search' && (
              <Alert severity="info">Loaded {loadedIds.length} matching promo IDs.</Alert>
            )}
            <Box sx={{ width: { xs: '100%', sm: 340 } }}>
              <DateTimeWidget
                label="2. New expiry date and time"
                value={expiresAt}
                onChange={(value) => {
                  setExpiresAt(value);
                  resetReview();
                }}
                required
                helperText="A 24-hour grace period applies after promo expiry."
              />
            </Box>
            <Button
              variant="text"
              disabled={
                isLoading ||
                isSubmitting ||
                (scope === 'search' && !loadedIds) ||
                (scope === 'page' && !currentPagePromos.length)
              }
              onClick={reviewUpdate}
            >
              Review expiry update
            </Button>
            {review && (
              <Stack spacing={1}>
                <Alert severity="info">
                  Update only the expiry date to {formatExpiry(review.expiresAt)} for {review.ids.length} promo code
                  {review.ids.length === 1 ? '' : 's'} in {Math.ceil(review.ids.length / PROMO_EXPIRY_BATCH_SIZE)}{' '}
                  request
                  {review.ids.length > PROMO_EXPIRY_BATCH_SIZE ? 's' : ''}.
                </Alert>
                {review.filters.length > 0 && (
                  <Typography variant="body2">Search filters: {review.filters.join('; ')}</Typography>
                )}
                <Box component="ul" sx={{ maxHeight: 180, overflowY: 'auto', mt: 0, pl: 3 }}>
                  {review.ids.map((id) => (
                    <Typography component="li" key={id} variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {id}
                    </Typography>
                  ))}
                </Box>
                <Button variant="contained" disabled={isSubmitting} onClick={() => setConfirmOpen(true)}>
                  Update {review.ids.length} promo code{review.ids.length === 1 ? '' : 's'}
                </Button>
              </Stack>
            )}
            {progress && (
              <Alert severity="info">
                Processed {progress.processed} of {progress.total}: {progress.updated} updated, {progress.failed}{' '}
                failed.
              </Alert>
            )}
            {completion && (
              <Alert severity={completion.failed ? 'warning' : 'success'}>
                Expiry update complete: {completion.updated} of {completion.total} updated
                {completion.failed ? `, ${completion.failed} failed` : ''}.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
              <DialogTitle>Confirm bulk expiry update</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Set the expiry date for {review?.ids.length ?? 0} promo codes to{' '}
                  {review ? formatExpiry(review.expiresAt) : ''}?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={() => void submitUpdate()}>
                  Update expiry date
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </PromoFeature>
  );
}
